/**
 * Os chevrons de etapa do modal do Negócio (11.4, DESIGN.md `chevron-de-etapa`).
 *
 * A fileira mostra **só as etapas do funil atual**, na ordem do catálogo: a atual em **dourado**
 * com o navy por cima e os dias sem contato ao lado; as demais esmaecidas, na cor pastel da etapa
 * quando ela tem uma (protótipo v2, rodada 4 — a que o Gustavo aprovou). Clicar numa
 * etapa move o Negócio para ela — para a frente ou para trás (é o único gesto de mover no celular).
 * Trocar de funil **não** é pelos chevrons. A etapa atual não é acionável: reenviá-la não gravaria
 * nada. No celular a fileira rola de lado (a mesma árvore; o `overflow-x` é do CSS).
 */

import type { Etapa } from './api'
import './chevrons-de-etapa.css'

/** A etapa atual não está nas etapas lidas do funil (catálogo desatualizado): não se adivinha. */
export const ETAPA_ATUAL_FORA_DO_CATALOGO =
  'A etapa deste negócio não está no catálogo lido. Feche e abra de novo para recarregar.'

export interface PropsDosChevrons {
  etapas: readonly Etapa[]
  etapaAtualId: string
  /** Os dias sem contato, já formatados ("4d"): entram pequenos na etapa atual (protótipo v2). */
  dias?: string
  /** Negócio fechado ou mutação em voo: nenhum chevron move. */
  desabilitado: boolean
  /** O `id` da frase que explica o bloqueio, quando há uma. */
  idDaRazao?: string
  aoMover: (etapaId: string) => void
}

export function ChevronsDeEtapa({
  etapas,
  etapaAtualId,
  dias,
  desabilitado,
  idDaRazao,
  aoMover,
}: PropsDosChevrons) {
  const posicaoAtual = etapas.findIndex((etapa) => etapa.id === etapaAtualId)
  /* Sem a atual na fileira, "passada/futura" não existe e todo chevron pareceria destino válido. */
  if (posicaoAtual === -1) {
    return (
      <p className="ficha__ajuda" role="status">
        {ETAPA_ATUAL_FORA_DO_CATALOGO}
      </p>
    )
  }
  return (
    <ol className="chevrons" aria-label="Etapas do funil">
      {etapas.map((etapa, posicao) => {
        const atual = etapa.id === etapaAtualId
        const momento = atual ? 'atual' : posicao < posicaoAtual ? 'passada' : 'futura'
        return (
          <li key={etapa.id} className="chevrons__item">
            <button
              type="button"
              className="chevrons__etapa"
              data-momento={momento}
              data-cor-da-etapa={etapa.cor ?? 'nenhuma'}
              aria-current={atual ? 'step' : undefined}
              disabled={atual || desabilitado}
              aria-describedby={desabilitado ? idDaRazao : undefined}
              onClick={() => {
                aoMover(etapa.id)
              }}
            >
              {etapa.nome}
              {atual && dias !== undefined ? (
                <small className="chevrons__dias numero" aria-hidden="true">
                  {dias}
                </small>
              ) : null}
            </button>
          </li>
        )
      })}
    </ol>
  )
}
